import { useState } from "react";
import { Link } from "react-router-dom";
import useSWR, { mutate } from "swr";
import { toast } from "sonner";
import { Plus, FolderPlus, Layers, ArrowRight, Youtube } from "lucide-react";
import { createCollection, listCollections, formatNumber } from "@/lib/api";
import PlaylistImportWidget from "@/components/PlaylistImportWidget";
import usePageMeta from "@/hooks/use-page-meta";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export default function Collections() {
  const { data, isLoading } = useSWR("/collections", () => listCollections());
  usePageMeta({
    title: "Collections — Curated Playlists of tobiasdeamon Videos",
    description:
      "Every YouTube playlist and curated shelf from the tobiasdeamon Vault — group by hardware, by year, or by game series.",
  });
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    if (!title.trim()) {
      toast.error("Give your collection a name");
      return;
    }
    setSaving(true);
    try {
      const c = await createCollection({ title, description });
      toast.success("Collection created");
      setOpen(false);
      setTitle("");
      setDescription("");
      await mutate("/collections");
      window.location.href = `/collections/${c.slug}`;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create collection");
    } finally {
      setSaving(false);
    }
  };

  const items = data?.items || [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
      <div className="flex items-end justify-between flex-wrap gap-4 sm:gap-6 mb-8 sm:mb-10">
        <div>
          <div className="mono-label">Curator</div>
          <h1 className="font-serif-editorial text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#f4f1ea] mt-3">
            Collections.
          </h1>
          <p className="text-white/60 mt-3 sm:mt-4 max-w-2xl text-sm sm:text-base">
            Group videos into named playlists — "PC Benchmarks 2024", "Best of Pet Rescue",
            anything. Each collection has its own shareable link.
          </p>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="new-collection-btn"
              className="rounded-full bg-[#d95d39] hover:bg-[#c14e2e] text-white font-mono-tech text-[11px] uppercase tracking-[0.22em] px-6 py-6"
            >
              <FolderPlus className="w-4 h-4 mr-2" /> New collection
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#12141d] border-white/10 text-[#f4f1ea]">
            <DialogHeader>
              <DialogTitle className="font-serif-editorial text-2xl">
                Create a collection
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <label className="mono-label text-white/50">Title</label>
                <Input
                  data-testid="new-collection-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. PC Benchmarks 2024"
                  className="mt-2 bg-[#0c0e15] border-white/10 focus-visible:ring-[#d95d39]"
                />
              </div>
              <div>
                <label className="mono-label text-white/50">Description (optional)</label>
                <Textarea
                  data-testid="new-collection-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="A short blurb about what this collection holds…"
                  rows={3}
                  className="mt-2 bg-[#0c0e15] border-white/10 focus-visible:ring-[#d95d39]"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                onClick={handleCreate}
                disabled={saving}
                data-testid="new-collection-submit"
                className="rounded-full bg-[#d95d39] hover:bg-[#c14e2e] text-white font-mono-tech text-[11px] uppercase tracking-[0.22em]"
              >
                <Plus className="w-4 h-4 mr-2" /> Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="aspect-[4/3] rounded-2xl bg-white/[0.03] animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="space-y-6">
          <PlaylistImportWidget />
          <div className="rounded-2xl border border-dashed border-white/15 bg-[#0c0e15] p-16 text-center">
            <Layers className="w-8 h-8 text-[#d95d39] mx-auto" />
            <p className="font-serif-editorial text-3xl text-[#f4f1ea] mt-6">
              No collections yet.
            </p>
            <p className="text-white/50 mt-2">
              Curate your first named playlist and share the link with anyone —
              or import every YouTube playlist above.
            </p>
            <Button
              onClick={() => setOpen(true)}
              data-testid="empty-new-collection-btn"
              className="mt-8 rounded-full bg-[#d95d39] hover:bg-[#c14e2e] text-white font-mono-tech text-[11px] uppercase tracking-[0.22em]"
            >
              <FolderPlus className="w-4 h-4 mr-2" /> Create your first
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-8">
          <PlaylistImportWidget />
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            data-testid="collections-grid"
          >
            {items.map((c) => (
              <Link
                key={c.slug}
                to={`/collections/${c.slug}`}
                data-testid={`collection-card-${c.slug}`}
                className="group hover-lift block rounded-2xl border border-white/10 bg-[#12141d] overflow-hidden"
              >
                <CollectionCover thumbnails={c.preview_thumbnails} />
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <span className="mono-label text-white/40">
                      {formatNumber(c.count)} videos
                    </span>
                    {c.source === "youtube" && (
                      <span
                        title="Imported from YouTube"
                        className="mono-label text-[#d95d39] inline-flex items-center gap-1"
                      >
                        <Youtube className="w-3 h-3" /> YT
                      </span>
                    )}
                    <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-[#d95d39] group-hover:translate-x-1 transition-all" />
                  </div>
                  <h3 className="mt-2 font-serif-editorial text-2xl text-[#f4f1ea] group-hover:text-[#d95d39] transition-colors line-clamp-2">
                    {c.title}
                  </h3>
                  {c.description && (
                    <p className="mt-2 text-sm text-white/50 line-clamp-2">{c.description}</p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CollectionCover({ thumbnails = [] }) {
  if (!thumbnails.length) {
    return (
      <div className="aspect-video bg-gradient-to-br from-[#1a1d29] to-[#0c0e15] flex items-center justify-center">
        <Layers className="w-8 h-8 text-white/20" />
      </div>
    );
  }
  if (thumbnails.length === 1) {
    return (
      <div className="aspect-video bg-black">
        <img src={thumbnails[0]} alt="" className="w-full h-full object-cover" />
      </div>
    );
  }
  const grid = thumbnails.slice(0, 4);
  return (
    <div className="aspect-video grid grid-cols-2 gap-0.5 bg-black">
      {grid.map((t, i) => (
        <div key={i} className="overflow-hidden">
          <img src={t} alt="" className="w-full h-full object-cover" />
        </div>
      ))}
    </div>
  );
}
